import { Box } from '@mui/material'
import { useRef } from 'react'

import { CommentBoxFragment, Maybe, User } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  CommentList,
  CommentListProps,
  ReplyBox,
  ReplyFormRef,
  ReplyFormValues,
  ThreadDetailBoxProps,
} from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  user: Maybe<
    Pick<User, 'id' | 'accountId' | 'formattedAccountId' | 'email' | 'avatar'>
  >
  rootCollectionId?: string
  artistId: string
  comment: Maybe<Omit<CommentBoxFragment, 'replies'>>
  /**
   * @todo
   * comment周りの型を整理しなければいけないので別タスクで型整理します
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  replies: any
}

export type Action = {
  onFetchMore: () => Promise<void>
  onReply: (values: ReplyFormValues, artistId: string) => Promise<void>
  onLikeOrUnlikeComment: (input: {
    isLike: boolean
    commentId: string
    id?: string
    likeCount: number
  }) => Promise<void>
  onDeleteComment: CommentListProps['onDeleteComment']
  onBlockUser: ThreadDetailBoxProps['onBlockUser']
  onReportUser: ThreadDetailBoxProps['onReportUser']
}

export const ReplyDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onReply,
  onLikeOrUnlikeComment,
  onDeleteComment,
  onBlockUser,
  onReportUser,
}) => {
  const { routerNames } = useRouter<{
    threadId: string
    commentId: string
  }>()
  const replyFormRef = useRef<ReplyFormRef>(null)

  const { t } = useTranslation()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ReplyDetail} />

      <BaseLayout
        header={{ title: t('reply'), back: true }}
        navigation={{
          type: 'replyForm',
          props: {
            ref: replyFormRef,
            onReply: values => onReply(values, state.artistId ?? ''),
            userId: state.user?.id ?? '',
            replyToDisplayName: state.comment?.commenter?.displayName,
            visibleImage: true,
          },
        }}
      >
        <Box sx={{ px: 4, pt: 5, mb: 5 }}>
          {state?.comment && (
            <ReplyBox
              comment={state?.comment}
              divider
              userId={state?.user?.id ?? ''}
              onDeleteComment={onDeleteComment}
              onReportUser={onReportUser}
              onBlockUser={onBlockUser}
              onLikeOrUnlikeComment={onLikeOrUnlikeComment}
              onClickReply={() => replyFormRef.current?.focus()}
              rootCollectionId={state.rootCollectionId}
              showReaction
              onReplyToReply={() => {}}
            />
          )}
        </Box>
        {state?.comment && (
          <CommentList
            commentCount={state.comment?.replyCount}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            comments={state.replies}
            user={state.user}
            onFetchMore={onFetchMore}
            onDeleteComment={onDeleteComment}
            onReportUser={onReportUser}
            onBlockUser={onBlockUser}
            onLikeOrUnlikeComment={onLikeOrUnlikeComment}
            rootCollectionId={state.rootCollectionId}
            hiddenHeader
            hiddenNotice
            hiddenReply
            showReaction
            inverted
            onReplyToReply={() => {}}
            onClickReply={() => {}}
          />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
